import { useState, useRef, useEffect, KeyboardEvent, ClipboardEvent, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Delete } from 'lucide-react';

interface PinInputProps {
  length?: number;
  onComplete: (pin: string) => void;
  onValueChange?: (pin: string) => void;
  error?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  showKeypad?: boolean;
  className?: string;
}

export function PinInput({
  length = 4,
  onComplete,
  onValueChange,
  error,
  disabled = false,
  autoFocus = true,
  showKeypad = true,
  className,
}: PinInputProps) {
  const [values, setValues] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset values when length changes
  useEffect(() => {
    setValues(Array(length).fill(''));
  }, [length]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0] && !showKeypad) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus, showKeypad]);

  const currentPin = values.join('');
  const filledCount = values.filter(v => v !== '').length;
  const isComplete = filledCount === length;

  useEffect(() => {
    onValueChange?.(currentPin);
  }, [currentPin, onValueChange]);

  // Auto-submit when all digits entered
  useEffect(() => {
    if (isComplete && !isSubmitting) {
      setIsSubmitting(true);
      onComplete(currentPin);
      // Reset after a short delay to allow for error display
      setTimeout(() => setIsSubmitting(false), 100);
    }
  }, [isComplete, currentPin, onComplete, isSubmitting]);

  const handleChange = (index: number, value: string) => {
    if (disabled) return;

    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);

    const newValues = [...values];
    newValues[index] = digit;
    setValues(newValues);

    // Move to next input if digit entered
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    if (e.key === 'Backspace') {
      e.preventDefault();
      const newValues = [...values];

      if (values[index]) {
        // Clear current field
        newValues[index] = '';
        setValues(newValues);
      } else if (index > 0) {
        // Move to previous field and clear it
        newValues[index - 1] = '';
        setValues(newValues);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);

    if (pastedData) {
      const newValues = [...values];
      for (let i = 0; i < pastedData.length; i++) {
        newValues[i] = pastedData[i];
      }
      setValues(newValues);

      // Focus on the next empty field or last field
      const nextEmptyIndex = newValues.findIndex(v => !v);
      const focusIndex = nextEmptyIndex === -1 ? length - 1 : nextEmptyIndex;
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleFocus = (index: number) => {
    // Select the content when focused
    inputRefs.current[index]?.select();
  };

  const clear = useCallback(() => {
    setValues(Array(length).fill(''));
    inputRefs.current[0]?.focus();
  }, [length]);

  // Keypad handlers
  const handleKeypadDigit = useCallback((digit: string) => {
    if (disabled) return;

    // Find first empty slot
    const emptyIndex = values.findIndex(v => v === '');
    if (emptyIndex === -1) return; // All filled

    const newValues = [...values];
    newValues[emptyIndex] = digit;
    setValues(newValues);
  }, [disabled, values]);

  const handleKeypadBackspace = useCallback(() => {
    if (disabled) return;

    // Find last filled slot
    let lastFilledIndex = -1;
    for (let i = values.length - 1; i >= 0; i--) {
      if (values[i] !== '') {
        lastFilledIndex = i;
        break;
      }
    }

    if (lastFilledIndex === -1) return; // All empty

    const newValues = [...values];
    newValues[lastFilledIndex] = '';
    setValues(newValues);
  }, [disabled, values]);

  return (
    <div className={cn('flex flex-col items-center gap-4', className)}>
      {/* PIN Display */}
      <div className="flex gap-2">
        {Array.from({ length }).map((_, index) => (
          <input
            key={index}
            ref={el => {
              inputRefs.current[index] = el;
            }}
            type="password"
            inputMode="numeric"
            maxLength={1}
            value={values[index]}
            onChange={e => handleChange(index, e.target.value)}
            onKeyDown={e => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={() => handleFocus(index)}
            disabled={disabled}
            className={cn(
              'w-10 h-12 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-semibold rounded-lg border-2 transition-all',
              'focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary',
              'bg-background text-foreground',
              disabled && 'opacity-50 cursor-not-allowed',
              error && 'border-destructive focus:ring-destructive focus:border-destructive',
              !error && 'border-border hover:border-muted-foreground/50',
              // Highlight filled vs empty
              values[index] ? 'border-primary/50' : ''
            )}
            aria-label={`PIN digit ${index + 1}`}
          />
        ))}
      </div>

      {error && (
        <p className="text-sm text-destructive animate-in fade-in slide-in-from-top-1">
          {error}
        </p>
      )}

      {/* Numeric Keypad */}
      {showKeypad && (
        <div className="grid grid-cols-3 gap-2 mt-2">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(digit => (
            <Button
              key={digit}
              type="button"
              variant="outline"
              size="lg"
              className="w-16 h-14 text-xl font-semibold"
              onClick={() => handleKeypadDigit(digit)}
              disabled={disabled || filledCount >= length}
            >
              {digit}
            </Button>
          ))}

          {/* Bottom row: Clear, 0, Backspace/Submit */}
          <Button
            type="button"
            variant="ghost"
            size="lg"
            className="w-16 h-14 text-sm text-muted-foreground"
            onClick={clear}
            disabled={disabled || filledCount === 0}
          >
            Clear
          </Button>

          <Button
            type="button"
            variant="outline"
            size="lg"
            className="w-16 h-14 text-xl font-semibold"
            onClick={() => handleKeypadDigit('0')}
            disabled={disabled || filledCount >= length}
          >
            0
          </Button>

          <Button
            type="button"
            variant="outline"
            size="lg"
            className="w-16 h-14"
            onClick={handleKeypadBackspace}
            disabled={disabled || filledCount === 0}
          >
            <Delete className="h-5 w-5" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default PinInput;
